import React, { useCallback } from 'react';
import { TableRow, TableCell, ActionsTableCell, Pencil, Duplicate, Trash } from '@hqo/react-components-library';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import styled from 'styled-components';
import { Instance, InstanceOwnerType } from '../../../store/instances/types';
import moment from 'moment';
import { useShowDeleteInstanceConfigDialogHandler } from 'store/instances/hooks';

const EditIcon = styled(Pencil)`
  width: 18px;
  height: 18px;
`;

const CopyIcon = styled(Duplicate)<{ $disabled?: boolean }>`
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  color: ${({ $disabled, theme }) => ($disabled ? theme.colors.greys.lightGrey2 : theme.colors.greys.adminGreyDark)};
`;

const DeleteIcon = styled(Trash)`
  cursor: pointer;
`;

const HoverTableRow = styled(TableRow)`
  &:hover {
    background: #f5f7fa;
  }
`;

interface InstancesTableRowProps {
  instance: Instance;
  onCopy: (instance: Instance) => void;
}

export const InstancesTableRow = ({ instance, onCopy }: InstancesTableRowProps): JSX.Element => {
  const instanceUuid = instance.uuid;
  const editInstanceUrl = `instances/${instanceUuid}`;
  const handleDelete = useShowDeleteInstanceConfigDialogHandler(instanceUuid);
  const canCopy = instance.owner_type === InstanceOwnerType.BUILDING;

  const handleCopy = useCallback(() => {
    if (canCopy) {
      onCopy(instance);
    }
  }, [canCopy, instance, onCopy]);

  return (
    <HoverTableRow data-cy={`instance-row-${instanceUuid}`}>
      <TableCell data-cy="created-at">{moment(instance.created_at).format('MM-DD-YYYY').toString()}</TableCell>
      <TableCell data-cy="building-name">{instance.building?.name ?? '-'}</TableCell>
      <TableCell data-cy="developer-name">{instance.company?.name ?? '-'}</TableCell>
      <TableCell data-cy="app-name">{instance.app?.name ?? '-'}</TableCell>
      <TableCell data-cy="owner-type">
        <FormattedMessage id={`instanceTypes.${instance.owner_type.toLowerCase()}`} />
      </TableCell>
      <TableCell data-cy="status">
        <FormattedMessage id={`instanceStatuses.${instance.status.toLowerCase()}`} />
      </TableCell>
      <ActionsTableCell>
        <Link to={editInstanceUrl}>
          <EditIcon variant="adminGreyDark" data-cy="instance-edit-link" />
        </Link>
      </ActionsTableCell>
      <ActionsTableCell>
        <CopyIcon variant="adminGreyDark" onClick={handleCopy} $disabled={!canCopy} data-cy="copy-icon" />
      </ActionsTableCell>
      <ActionsTableCell>
        <DeleteIcon variant="adminGreyDark" onClick={handleDelete} data-cy="delete-icon" />
      </ActionsTableCell>
    </HoverTableRow>
  );
};
